/**
 * <PathTube> — renders a Tube along a polyline of Vector3 samples. Used by
 * Stations / Dimensions / InfoBars.
 *
 * We use CatmullRomCurve3 to smooth the polyline before generating the
 * TubeGeometry. The tube radius is half of the configured thickness.
 */

import React, { useMemo } from 'react';
import {
  CatmullRomCurve3,
  TubeGeometry,
  type Vector3,
  Color,
  DoubleSide,
} from 'three';

interface PathTubeProps {
  points: Vector3[];
  thicknessMm: number;
  color: string | number;
  opacity?: number;
  segmentsPerStep?: number;
  radialSegments?: number;
}

export const PathTube: React.FC<PathTubeProps> = ({
  points,
  thicknessMm,
  color,
  opacity = 1,
  segmentsPerStep = 1,
  radialSegments = 8,
}) => {
  const geometry = useMemo(() => {
    if (points.length < 2) return null;
    const curve = new CatmullRomCurve3(points, false, 'catmullrom', 0.0);
    const segments = Math.max(8, points.length * segmentsPerStep);
    return new TubeGeometry(curve, segments, thicknessMm / 2, radialSegments, false);
  }, [points, thicknessMm, segmentsPerStep, radialSegments]);

  const colorObj = useMemo(() => new Color(color as string), [color]);

  if (!geometry) return null;
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color={colorObj}
        transparent={opacity < 1}
        opacity={opacity}
        side={DoubleSide}
        roughness={0.6}
        metalness={0.1}
      />
    </mesh>
  );
};
